import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff } from "lucide-react";

export default function SaLoginPage() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ username: "", password: "" });
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const payload = {
                username: form.username.trim(),
                password: form.password.trim(),
            };
            const { data } = await api.post("/admin/login", payload);
            // Persist token so all subsequent /admin/* calls work even when
            // the cookie is blocked cross-origin (custom domain).
            if (data?.token) {
                try {
                    localStorage.setItem("sf_admin_token", data.token);
                } catch (_) {}
            }
            navigate("/sa/cms");
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-navy flex items-center justify-center p-6"
            data-testid="sa-login-page"
        >
            <div className="bg-grid absolute inset-0 opacity-20 pointer-events-none"></div>
            <form
                onSubmit={submit}
                className="w-full max-w-sm bg-white rounded-2xl p-10 relative z-10 shadow-2xl"
                data-testid="sa-login-form"
            >
                <div className="w-12 h-12 rounded-lg bg-amber-brand text-navy flex items-center justify-center mb-6">
                    <Lock size={22} />
                </div>
                <div className="label-eyebrow mb-2">Area riservata</div>
                <h1 className="font-display text-3xl text-navy mb-6">
                    Staff Flow Admin
                </h1>
                {error && (
                    <div
                        className="mb-4 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md"
                        data-testid="sa-login-error"
                    >
                        {error}
                    </div>
                )}
                <div className="space-y-4 mb-6">
                    <div>
                        <Label htmlFor="username">Username</Label>
                        <Input
                            id="username"
                            value={form.username}
                            onChange={(e) =>
                                setForm({ ...form, username: e.target.value })
                            }
                            required
                            autoComplete="off"
                            autoCapitalize="none"
                            spellCheck={false}
                            className="mt-2"
                            data-testid="sa-login-username"
                        />
                    </div>
                    <div>
                        <Label htmlFor="password">Password</Label>
                        <div className="relative mt-2">
                            <Input
                                id="password"
                                type={showPw ? "text" : "password"}
                                value={form.password}
                                onChange={(e) =>
                                    setForm({ ...form, password: e.target.value })
                                }
                                required
                                autoComplete="off"
                                spellCheck={false}
                                className="pr-10"
                                data-testid="sa-login-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-sf-muted hover:text-navy"
                                aria-label={showPw ? "Nascondi password" : "Mostra password"}
                                data-testid="sa-login-toggle-pw"
                                tabIndex={-1}
                            >
                                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>
                </div>
                <Button
                    type="submit"
                    disabled={busy}
                    className="w-full bg-navy hover:bg-navy/90 text-white lift"
                    data-testid="sa-login-submit"
                >
                    {busy ? "Accesso…" : "Entra"}
                </Button>
                <div className="mt-6 text-xs text-sf-muted text-center">
                    Solo personale autorizzato.
                </div>
            </form>
        </div>
    );
}
